import {
  ButtonDark,
  CaseCard,
  FeaturedCase,
  IMG,
  PageHero,
  Reveal,
  Section,
  SectionHeader,
  FinalCta,
} from "../lib/shared"
import { StepFlow } from "../lib/visuals"
import { useCollection } from "../lib/content"
import { Seo, breadcrumbSchema, itemListSchema } from "../lib/seo"

const ANATOMY = [
  { n: "01", t: "The problem", d: "What the organisation needed to solve, and why it mattered." },
  { n: "02", t: "The approach", d: "The key decisions we made, and how the solution is put together." },
  { n: "03", t: "The build", d: "What we delivered, and how it fits into the way people work." },
  { n: "04", t: "The outcome", d: "What it does now, with figures only where they have been verified." },
]

export default function Work() {
  const { items } = useCollection("caseStudies")
  const works = items.filter((w) => w.status === "Published")
  const [featured, ...rest] = works

  return (
    <>
      <Seo
        title="Selected work and case studies"
        description="Software, platforms and digital systems Sableboxx has built, with the problem behind each one, how it works and what it delivered."
        jsonLd={[
          itemListSchema(
            works.map((w) => ({ name: w.title, path: `/work/${w.slug}` })),
            "Sableboxx case studies",
          ),
          breadcrumbSchema([{ name: "Work", path: "/work" }]),
        ]}
      />
      <PageHero
        eyebrow="Our work"
        title="Systems built for real operations"
        intro="Software, platforms and digital presence we've built for organisations in Nigeria and beyond, with the problem behind each one, how it works and what it delivered."
        image={IMG.workHero}
      />

      <Section tone="paper">
        {featured ? (
          <>
            <Reveal>
              <FeaturedCase work={featured} />
            </Reveal>
            {rest.length > 0 && (
              <div className="mt-20 grid grid-cols-1 gap-6 border-t border-neutral-200 pt-20 sm:grid-cols-2 lg:mt-24 lg:pt-24">
                {rest.map((w, i) => (
                  <Reveal key={w.id} delay={(i % 2) * 90}>
                    <CaseCard work={w} />
                  </Reveal>
                ))}
              </div>
            )}
          </>
        ) : (
          <Reveal>
            <div className="rounded-2xl border border-neutral-200 bg-mist px-8 py-16 lg:text-center">
              <h2 className="text-[24px] font-bold tracking-[-0.02em]">
                Case studies are being written up
              </h2>
              <p className="mt-3 max-w-md lg:mx-auto text-[16px] leading-[1.7] text-neutral-600">
                In the meantime, we&rsquo;re happy to walk you through recent
                projects on a call.
              </p>
              <div className="mt-8 flex lg:justify-center">
                <ButtonDark to="/contact">Get in touch</ButtonDark>
              </div>
            </div>
          </Reveal>
        )}
      </Section>

      {/* How each case study is written */}
      <Section tone="mist">
        <SectionHeader
          eyebrow="How we write them up"
          title="Evidence you can assess"
          intro="Every case study answers the same four questions, so you can judge the thinking as well as the result."
        />
        <StepFlow steps={ANATOMY} className="sm:grid-cols-2 lg:grid-cols-4" />
      </Section>

      <FinalCta />
    </>
  )
}
